import type { CarePlan, Composition, Reference } from '@medplum/fhirtypes'
import { CarePlanSnapshot } from '@seen/core/models/care-plans/CarePlanSnapshot'
import { CarePlanState } from '@seen/core/models/care-plans/constants'
import { mapDemographicsQuestionnaireResponses } from '@seen/core/models/care-plans/demographics'
import { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { Intervention } from '@seen/core/models/care-plans/intervention'
import {
  ExtendedQuestionnaireResponse,
  type QuestionnaireWithResponse,
} from '@seen/core/models/questionnaireResponse'
import { CarePlanParticipantSignatureTask } from '@seen/core/models/tasks/signature/carePlanParticipantSignatureTask'
import { CarePlanSignatureTask } from '@seen/core/models/tasks/signature/carePlanSignatureTask'
import { groupBy, map, sortBy } from 'lodash-es'
import { useMemo } from 'react'
import { useBulkReadVersions } from '#src/hooks/useBulkReadVersions.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useReadVersion } from '#src/hooks/useReadVersion.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface UseCarePlanSnapshotDataResult {
  carePlan: InitialCarePlan | undefined
  goalCarePlans: GoalCarePlan[]
  signatures: CarePlanSignatureTask[]
  carePlanParticipantSignatures: CarePlanParticipantSignatureTask[]
  interventionsByGoalCarePlan: {
    goalCarePlan: GoalCarePlan
    interventions: Intervention[]
  }[]
  demographics: QuestionnaireWithResponse[]
  isLoading: boolean
  error: Error | null
}

/**
 * Custom hook to load all data required for the CarePlanSnapshotPage and its child components
 * for a CarePlanSnapshot. This hook uses the basedOn attribute of the CarePlanSnapshot
 * to load the original InitialCarePlan, GoalCarePlans, and Interventions.
 *
 * This mirrors the structure of useCarePlanData, but is used for the CarePlanSnapshotPage
 *
 * @param carePlanSnapshotReference - The reference of the care plan snapshot to load.
 * @returns An object containing all the loaded data, matching the same structure as useCarePlanData.
 */
export function useCarePlanSnapshotData(
  carePlanSnapshotReference?: Reference<Composition>
): UseCarePlanSnapshotDataResult {
  const carePlanSnapshotQuery = useRead(
    CarePlanSnapshot,
    carePlanSnapshotReference
  )
  const carePlanSnapshot = carePlanSnapshotQuery.data

  const initialCarePlanQuery = useReadVersion(
    InitialCarePlan,
    carePlanSnapshot?.initialCarePlan as Reference<CarePlan>,
    {
      query: {
        enabled: carePlanSnapshotQuery.isSuccess,
        select: (carePlan) => {
          // Mark the care plan as signed off and non-editable
          carePlan.state = CarePlanState.SignedOff
          return carePlan
        },
      },
    }
  )

  const goalCarePlansQuery = useBulkReadVersions(
    GoalCarePlan,
    carePlanSnapshot?.goalCarePlans ?? [],
    {
      query: {
        enabled: carePlanSnapshotQuery.isSuccess,
        select: (gcps) =>
          gcps.map((gcp) => {
            // Mark all goal care plans as signed off and non-editable
            gcp.state = CarePlanState.SignedOff
            return gcp
          }),
      },
    }
  )

  const interventionsQuery = useBulkReadVersions(
    Intervention,
    carePlanSnapshot?.interventions ?? [],
    {
      query: {
        enabled: carePlanSnapshotQuery.isSuccess,
        select: (interventions) =>
          interventions.map((intervention) => {
            // Mark all interventions as signed off and non-editable
            intervention.state = CarePlanState.SignedOff
            return intervention
          }),
      },
    }
  )

  const demographicsQuery = useBulkReadVersions(
    ExtendedQuestionnaireResponse,
    carePlanSnapshot?.demographics ?? [],
    {
      query: {
        enabled: carePlanSnapshotQuery.isSuccess,
      },
    }
  )

  const interventionsByGoalCarePlan = useMemo(() => {
    const goalCarePlans = goalCarePlansQuery.data ?? []
    const interventions = interventionsQuery.data ?? []
    return map(
      groupBy(interventions, (i) => i.goalCarePlan?.reference),
      (interventions, groupCarePlanReference) => {
        const goalCarePlan = goalCarePlans.find(
          (g) => g.reference.reference === groupCarePlanReference
        )
        if (!goalCarePlan) {
          console.warn(
            `GoalCarePlan not found for groupCarePlanReference: ${groupCarePlanReference}`
          )
          return null
        }

        return {
          goalCarePlan,
          interventions: sortBy(interventions, (i) => i.sortOrder),
        }
      }
    ).filter((i) => i !== null)
  }, [goalCarePlansQuery.data, interventionsQuery.data])

  const demographics = useMemo(
    () => mapDemographicsQuestionnaireResponses(demographicsQuery.data ?? []),
    [demographicsQuery.data]
  )

  const signaturesQuery = useSearch(
    CarePlanSignatureTask,
    CarePlanSignatureTask.referencesQuery(carePlanSnapshot?.signatures ?? []),
    {
      query: { enabled: carePlanSnapshotQuery.isSuccess },
    }
  )

  const carePlanParticipantSignatureQuery = useSearch(
    CarePlanParticipantSignatureTask,
    CarePlanParticipantSignatureTask.referencesQuery(
      carePlanSnapshot?.participantSignatures ?? []
    ),
    {
      query: { enabled: carePlanSnapshotQuery.isSuccess },
    }
  )

  const isLoading =
    carePlanSnapshotQuery.isLoading ||
    initialCarePlanQuery.isLoading ||
    goalCarePlansQuery.isLoading ||
    interventionsQuery.isLoading ||
    signaturesQuery.isLoading ||
    carePlanParticipantSignatureQuery.isLoading ||
    demographicsQuery.isLoading

  const error =
    carePlanSnapshotQuery.error ??
    initialCarePlanQuery.error ??
    goalCarePlansQuery.error ??
    interventionsQuery.error ??
    signaturesQuery.error ??
    carePlanParticipantSignatureQuery.error ??
    demographicsQuery.error

  return {
    carePlan: initialCarePlanQuery.data,
    goalCarePlans: goalCarePlansQuery.data ?? [],
    signatures: signaturesQuery.data ?? [],
    carePlanParticipantSignatures: carePlanParticipantSignatureQuery.data ?? [],
    interventionsByGoalCarePlan,
    demographics,
    isLoading,
    error,
  }
}
